import Typography from '@mui/material/Typography';
import { format, isBefore, isSameDay } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar10, FlagFilled } from 'shared/assets/icons';
import { TimeLeft } from 'shared/components/ui/time-left';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { Goal } from 'shared/types/goal';

import * as Styled from './goal-item-card.style';

export type DeadlineProps = {
  goal: Goal;
  showMissingDates?: boolean;
};

export const Deadline: React.FC<DeadlineProps> = ({
  goal,
  showMissingDates,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const today = useToday();
  const user = useUser();
  const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;

  const hasTimeframe = !goal.completedAt && !!goal.startDate && !!goal.deadline;

  const started = useMemo(
    () =>
      !goal.startDate ||
      isBefore(goal.startDate, today) ||
      isSameDay(goal.startDate, today),
    [goal.startDate, today],
  );

  const tooltip = useMemo(() => {
    if (!goal.startDate && !goal.deadline) {
      return;
    }

    return [
      !started && goal.startDate
        ? format(goal.startDate, dateFormat)
        : undefined,
      goal.deadline ? format(goal.deadline, dateFormat) : undefined,
    ]
      .filter(Boolean)
      .join(' - ');
  }, [dateFormat, goal.deadline, goal.startDate, started]);

  if (!hasTimeframe && !showMissingDates) {
    return null;
  }

  return !goal.startDate && !goal.deadline ? (
    <Styled.NoDatesContainer>
      <Typography variant="buttonSmall">
        {t('general.labels.dates.no-dates')}
      </Typography>
    </Styled.NoDatesContainer>
  ) : (
    <Styled.DeadlineContainer ref={containerRef}>
      {started && goal.deadline ? (
        <TimeLeft
          date={goal.deadline!}
          isShort
          startIcon={FlagFilled}
          isSmallIcon
        />
      ) : !!goal.startDate ? (
        <TimeLeft
          date={goal.startDate}
          isShort
          startIcon={Calendar10}
          noColor
          isSmallIcon
        />
      ) : null}

      {!!tooltip && <Tooltip label={tooltip} referenceElement={containerRef} />}
    </Styled.DeadlineContainer>
  );
};
